/**
 * A descrição do ticket editável depois de criado (pedido da PoolTrack, 03/10),
 * nas DUAS visões: o cartão "Descrição" da completa e a "Solicitação" da
 * compacta usam este lápis e este editor — a regra de quem pode e o que o
 * PATCH leva vivem num lugar só (description-edit, testado na API).
 *
 * - Quem pode: `tickets.edit`, a permissão das outras edições do ticket. Sem
 *   ela, o lápis não aparece (o front só esconde; quem barra é a API).
 * - Espelhado do TiFlux, fechado ou cancelado: o lápis aparece DESLIGADO com o
 *   motivo — a API recusaria (409 `ticket_mirrored` / 422 `ticket_not_open`).
 * - A ficha tem campos próprios desde a spec 031 (ticket-sheet-card): o editor
 *   não ensina mais a escrever `Telefone:`. Avisa (sem bloquear) quando o
 *   ticket está na fila do disparo e o disparo não acharia telefone
 *   (resolveDispatchPhone: a ficha, a descrição e, no ticket aberto à mão no
 *   ERP, o solicitante).
 */
import { useState } from 'react';
import DOMPurify from 'dompurify';
import { useQuery } from '@tanstack/react-query';
import { EMPTY_TICKET_SHEET, type TicketDetail } from '@erp/shared';
import { Pencil, TriangleAlert } from 'lucide-react';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ticketDispatchQuery } from './dispatch-queries';
import {
  descriptionDraftOf,
  descriptionEditBlock,
  descriptionPatch,
  dispatchNeedsPhone,
  dispatchPhoneWarning,
  isHtmlDescription,
} from './description-edit';
import { usePatchTicket } from './tickets-mutations';
import { errorMessage } from './ticket-format';

type DescribedTicket = Pick<
  TicketDetail,
  'ticketNumber' | 'description' | 'mirrored' | 'status' | 'requestorPhone' | 'createdVia' | 'sheet'
>;

/**
 * A ficha do ticket para o aviso (spec 031). Contra uma API anterior a ela o
 * detalhe não traz `sheet` — o SPA sobe antes da API (3 min contra 25) —, e o
 * aviso segue a descrição, como antes.
 */
const phoneInputOf = (ticket: DescribedTicket, description: string | null) => ({
  description,
  requestorPhone: ticket.requestorPhone,
  createdVia: ticket.createdVia,
  sheet: ticket.sheet ?? EMPTY_TICKET_SHEET,
});

/** o status do disparo deste ticket (a mesma consulta do bloco do disparo — sem requisição a mais) */
function useDispatchStatus(ticketNumber: number) {
  const q = useQuery(ticketDispatchQuery(ticketNumber));
  return q.data?.dispatch?.status ?? null;
}

/** O lápis da descrição: some sem `tickets.edit`; desligado, com o motivo, quando a API recusaria. */
export function DescriptionEditButton({
  ticket,
  onEdit,
  className,
}: {
  ticket: DescribedTicket;
  onEdit: () => void;
  className?: string;
}) {
  const { can, ready } = usePermissions();
  // enquanto o /me carrega, esconde (aparecer depois é melhor que sumir)
  if (!ready || !can('tickets.edit')) return null;
  const blocked = descriptionEditBlock(ticket);
  if (blocked) {
    return (
      <Tooltip>
        {/* botão desligado não recebe o mouse: o gatilho é o span */}
        <TooltipTrigger asChild>
          <span tabIndex={0} className={cn('inline-flex', className)} aria-label={blocked}>
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground"
              disabled
              aria-label="Editar descrição"
            >
              <Pencil />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-64">
          {blocked}
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className={cn('text-muted-foreground hover:text-foreground', className)}
      title="Editar descrição"
      aria-label="Editar descrição"
      onClick={onEdit}
    >
      <Pencil />
    </Button>
  );
}

/** Aviso do disparo sem telefone — fora do editor, para quem só olha o ticket. */
export function DescriptionPhoneWarning({
  ticket,
  className,
}: {
  ticket: DescribedTicket;
  className?: string;
}) {
  const status = useDispatchStatus(ticket.ticketNumber);
  if (!dispatchNeedsPhone(status, phoneInputOf(ticket, ticket.description))) return null;
  return <PhoneWarning createdVia={ticket.createdVia} className={className} />;
}

function PhoneWarning({
  createdVia,
  className,
}: {
  createdVia: DescribedTicket['createdVia'];
  className?: string;
}) {
  return (
    <p
      role="status"
      className={cn(
        'flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
        className
      )}
    >
      <TriangleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
      <span>{dispatchPhoneWarning(createdVia)}</span>
    </p>
  );
}

/**
 * A descrição para ler: a que veio do TiFlux é HTML (sanitizado — sem isso
 * seria XSS); a digitada no ERP é texto, e as quebras de linha ficam (como
 * HTML, as linhas da ficha viravam um parágrafo corrido).
 */
export function DescriptionText({ description }: { description: string | null }) {
  if (!description || description.trim() === '') {
    return <p className="text-sm text-muted-foreground">Sem descrição.</p>;
  }
  if (isHtmlDescription(description)) {
    return (
      <div
        className="max-w-none break-words text-sm leading-relaxed [&_a]:text-primary [&_a]:underline [&_p]:mb-2"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
      />
    );
  }
  return <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{description}</p>;
}

/**
 * O erro do salvar. `ticket_not_open` ganha texto aqui e não no mapa comum: o
 * portal compara o código cru que errorMessage devolve para ele.
 */
function saveErrorMessage(e: unknown): string {
  const code = e && typeof e === 'object' ? (e as { error?: unknown }).error : undefined;
  if (code === 'ticket_not_open') return 'O ticket foi fechado ou cancelado — reabra para editar a descrição.';
  return errorMessage(e);
}

/** O editor: o texto atual, a dica da ficha, o que a leitura entendeu e Salvar/Cancelar. */
export function DescriptionEditor({
  ticket,
  onDone,
}: {
  ticket: DescribedTicket;
  onDone: () => void;
}) {
  const status = useDispatchStatus(ticket.ticketNumber);
  const patch = usePatchTicket(ticket.ticketNumber);
  const [draft, setDraft] = useState(() => descriptionDraftOf(ticket.description));

  // spec 031: a ficha tem campos próprios (o cartão logo acima) — o editor
  // deixou de ensinar a escrever `Telefone:` e de mostrar "Como a ficha fica".
  // Quem escrever a linha ainda é lido (a camada da descrição). Fica o aviso.
  // o rascunho no lugar da descrição salva: o aviso acompanha o que se digita
  const needsPhone = dispatchNeedsPhone(status, phoneInputOf(ticket, draft));

  const save = () => {
    const body = descriptionPatch(ticket.description, draft);
    if (!body) {
      onDone();
      return;
    }
    patch.mutate(body, { onSuccess: onDone });
  };

  return (
    <div className="space-y-2.5">
      <Textarea
        aria-label="Descrição"
        className="min-h-40 text-sm leading-relaxed"
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            if (!patch.isPending) save();
          }
        }}
      />
      {isHtmlDescription(ticket.description) ? (
        <p className="text-xs text-muted-foreground">
          Esta descrição veio formatada do TiFlux. Aqui ela aparece como texto, uma linha por
          linha, e é assim que fica ao salvar.
        </p>
      ) : null}

      {needsPhone ? <PhoneWarning createdVia={ticket.createdVia} /> : null}
      {patch.isError ? (
        <p className="text-sm text-destructive">{saveErrorMessage(patch.error)}</p>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" disabled={patch.isPending} onClick={onDone}>
          Cancelar
        </Button>
        <Button size="sm" disabled={patch.isPending} onClick={save}>
          Salvar
        </Button>
      </div>
    </div>
  );
}

/** O cartão "Descrição" da visão completa: título, lápis e o texto — ou o editor. */
export function DescriptionCard({ ticket }: { ticket: DescribedTicket }) {
  const [editing, setEditing] = useState(false);
  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Descrição</h2>
        {!editing ? <DescriptionEditButton ticket={ticket} onEdit={() => setEditing(true)} /> : null}
      </div>
      {editing ? (
        <DescriptionEditor ticket={ticket} onDone={() => setEditing(false)} />
      ) : (
        <div className="space-y-2">
          <DescriptionPhoneWarning ticket={ticket} />
          <DescriptionText description={ticket.description} />
        </div>
      )}
    </section>
  );
}
